import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, ExternalLink, Home as HomeIcon,
  AlertTriangle, Clock, Eye, EyeOff, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

function isMaster(user) {
  return user && (user.role === "master" || user.role === "admin");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("sv-SE"); } catch { return iso; }
}

function fmtKr(n) {
  if (n === null || n === undefined) return "—";
  return `${n.toLocaleString("sv-SE")} kr/mån`;
}

export default function ModPropertyWatch() {
  const token = sessionStorage.getItem("mod_token");
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");

  const [items, setItems] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [statusFilter, setStatusFilter] = useState("review");

  const fetchAll = useCallback(async (filter) => {
    setLoading(true);
    try {
      const params = filter && filter !== "all" ? { status: filter, limit: 200 } : { limit: 200 };
      const [it, rep] = await Promise.all([
        axios.get(`${API}/mod/property-watch/items`, { headers: modHeaders(), params }),
        axios.get(`${API}/mod/property-watch/reports/latest`, { headers: modHeaders() }),
      ]);
      setItems(it.data || []);
      setLatest(rep.data && rep.data.started_at ? rep.data : null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte hämta bostadsbevakning";
      toast.error(typeof msg === "string" ? msg : "Fel vid hämtning");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (token) fetchAll(statusFilter);
  }, [token, statusFilter, fetchAll]);

  if (!token) return <Navigate to="/mod/login" replace />;

  const canEdit = isMaster(user);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/property-watch/run`, {}, { headers: modHeaders(), timeout: 120000 }
      );
      const summary = `${data.new_count || 0} nya · ${data.removed_count || 0} försvunna · ${data.items_seen_again || 0} sedda igen`;
      toast.success("Bostadsbevakning klar", { description: summary, duration: 5000 });
      fetchAll(statusFilter);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Bostadsbevakning misslyckades";
      toast.error(typeof msg === "string" ? msg : "Bostadsbevakning misslyckades");
    }
    setRunning(false);
  };

  return (
    <div data-testid="mod-property-watch" className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <Link
        to="/mod"
        className="inline-flex items-center gap-1 text-bark hover:text-pine text-sm no-underline mb-4"
        data-testid="back-to-mod"
      >
        <ArrowLeft size={14} /> Tillbaka till moderationen
      </Link>

      <header className="mb-6">
        <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-1">
          Bostadsbevakning
        </h1>
        <p className="text-sm text-bark leading-relaxed max-w-2xl">
          Översikt över lediga hyresbostäder hämtade från Strömsunds Hyresbostäder.
          <strong> Strömsunds Hyresbostäder publiceras automatiskt på /bostader</strong> —
          mod kan ändå granska/dölja vid behov.
          Hemnet/Booli är endast manuella marknadskällor (visas inte här, se /mod/event-sources).
        </p>
      </header>

      {/* Info-banner */}
      <div className="mb-6 bg-bone-alt border border-warm-border rounded-sm p-4 flex items-start gap-3">
        <Info size={18} className="text-pine shrink-0 mt-0.5" />
        <div className="text-sm text-bark leading-relaxed">
          <p>
            <strong>Automatisk schemaläggning:</strong> bostadsbevakning körs varannan vecka (jämna
            ISO-veckor, måndag 06:00 UTC) tillsammans med källbevakning. Trigger manuellt nedan.
          </p>
          {latest && (
            <p className="mt-1.5 text-xs text-bark/80">
              Senaste körning: <span className="font-medium">{fmtDate(latest.started_at)}</span>
              {" · "}
              {latest.sources_checked || 0} källor, +{latest.new_count || 0} nya,
              {" "}{latest.removed_count || 0} försvunna, {latest.items_seen_again || 0} sedda igen.
            </p>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2" data-testid="status-filter">
          <span className="text-sm text-bark">Visa:</span>
          {[
            { v: "review", label: "Nya / granskas" },
            { v: "removed", label: "Försvunna" },
            { v: "all", label: "Alla" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setStatusFilter(opt.v)}
              data-testid={`filter-${opt.v}`}
              className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                statusFilter === opt.v
                  ? "bg-pine text-white border-pine"
                  : "bg-white text-bark border-warm-border hover:bg-bone-alt"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={runNow}
          disabled={running || !canEdit}
          data-testid="run-now-btn"
          className="inline-flex items-center gap-2 bg-falu hover:bg-falu-hover disabled:opacity-50 disabled:cursor-not-allowed text-parchment font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
          title={!canEdit ? "Endast master kan starta bostadsbevakning" : "Hämta lägenheter nu"}
        >
          <RefreshCw size={14} className={running ? "animate-spin" : ""} />
          {running ? "Bevakar…" : "Kör bostadsbevakning nu"}
        </button>
      </div>

      {/* Items lista */}
      {loading ? (
        <p className="text-muted-foreground py-6" data-testid="items-loading">Laddar…</p>
      ) : items.length === 0 ? (
        <div data-testid="items-empty" className="bg-bone-alt border border-warm-border rounded-sm p-8 text-center">
          <HomeIcon size={28} className="mx-auto text-pine/60 mb-2" />
          <p className="text-bark">
            {statusFilter === "review" && "Inga bostäder väntar på granskning just nu."}
            {statusFilter === "removed" && "Inga försvunna annonser registrerade."}
            {statusFilter === "all" && "Inga bostäder bevakade ännu — klicka \"Kör bostadsbevakning nu\"."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="items-list">
          {items.map((it) => (
            <PropertyCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* Senaste rapport */}
      <section className="mt-10" data-testid="latest-report">
        <h2 className="font-heading text-lg font-medium text-ink mb-3">
          Senaste bostadsbevakning-rapport
        </h2>
        {latest ? (
          <ReportSummary report={latest} />
        ) : (
          <div className="bg-bone-alt border border-warm-border rounded-sm p-4 text-sm text-muted-foreground">
            Ingen körning har gjorts ännu.
          </div>
        )}
      </section>
    </div>
  );
}

function PropertyCard({ item }) {
  const isRemoved = item.status === "removed";
  return (
    <div
      data-testid={`item-${item.id}`}
      className={`bg-white border rounded-sm p-4 ${isRemoved ? "border-warm-border bg-bone-alt/40 opacity-90" : "border-warm-border"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-heading text-base font-medium text-ink">{item.address}</h3>
            <StatusBadge status={item.status} />
            {item.area && (
              <span className="inline-block text-[10px] uppercase tracking-wider text-muted-foreground">
                {item.area}
              </span>
            )}
          </div>
          <div className="text-sm text-bark flex flex-wrap gap-x-4 gap-y-0.5">
            {item.rooms && <span>{item.rooms} rum</span>}
            {item.living_area && <span>{item.living_area} m²</span>}
            {item.rent && <span className="font-medium">{fmtKr(item.rent)}</span>}
            {item.available_from && <span>Tillgänglig: {item.available_from}</span>}
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
            <span>Källa: {item.source_name || "—"}</span>
            <span>Första gången sedd: {fmtDateOnly(item.first_seen_at)}</span>
            <span>Senast sedd: {fmtDateOnly(item.last_seen_at)}</span>
            {item.removed_at && (
              <span className="text-falu">Försvunnen: {fmtDateOnly(item.removed_at)}</span>
            )}
          </div>
        </div>
        <a
          href={item.detail_url || item.source_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`open-detail-${item.id}`}
          className="shrink-0 inline-flex items-center gap-1 text-xs border border-warm-border bg-white hover:bg-bone-alt text-bark px-2.5 py-1.5 rounded-sm transition-colors no-underline"
        >
          Öppna annons <ExternalLink size={12} />
        </a>
      </div>
      {isRemoved && (
        <div className="mt-3 pt-3 border-t border-warm-border/50 flex items-start gap-2 text-xs text-bark">
          <AlertTriangle size={13} className="text-falu shrink-0 mt-0.5" />
          <span>
            Annonsen är inte längre synlig hos källan. Vi påstår inte automatiskt att den är uthyrd —
            kontrollera manuellt om så är fallet.
          </span>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "removed") {
    return (
      <span data-testid="badge-removed" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-bark text-parchment">
        <EyeOff size={10} /> Ej längre annonserad
      </span>
    );
  }
  if (status === "review") {
    return (
      <span data-testid="badge-review" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-falu/15 text-falu">
        <Eye size={10} /> Granskning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-pine/15 text-pine">
      {status || "—"}
    </span>
  );
}

function ReportSummary({ report }) {
  const r = report || {};
  return (
    <div data-testid="report-summary" className="bg-white border border-warm-border rounded-sm p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
        <Clock size={14} className="text-muted-foreground" />
        <span className="font-medium text-ink">{fmtDate(r.started_at)}</span>
        {r.forced && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">(manuell)</span>}
      </div>
      <div className="flex flex-wrap gap-1.5 text-xs mb-3">
        <Pill color="pine">{r.sources_checked || 0} källor</Pill>
        <Pill color="pine">+{r.new_count || 0} nya</Pill>
        <Pill color="bark">{r.removed_count || 0} försvunna</Pill>
        <Pill color="muted">{r.items_seen_again || 0} sedda igen</Pill>
        {(r.sources_failed || 0) > 0 && <Pill color="falu">{r.sources_failed} fel</Pill>}
      </div>
      {(r.errors || []).length > 0 && (
        <div className="text-xs text-falu">
          <strong>Fel:</strong>
          <ul className="list-disc pl-5 mt-1 space-y-0.5">
            {(r.errors || []).map((e, i) => (
              <li key={i}>{e.source || "?"}: {e.msg}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Hemnet/Booli ingår inte i denna rapport — de bevakas endast via källbevakning (HEAD-anrop) i /mod/event-sources.
      </p>
    </div>
  );
}

function Pill({ color, children }) {
  const palette = {
    pine: "bg-pine/15 text-pine",
    bark: "bg-bark/15 text-bark",
    falu: "bg-falu/15 text-falu",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-block font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${palette[color] || palette.bark}`}>
      {children}
    </span>
  );
}
